import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import DictMappingDialog from '../../components/data-integration/DictMappingDialog';
import { getSyncedData } from '../../services/data-integration-api';

interface DataAuditProps {
  normalizedTableId: string;
  connectionId?: string;
  onBack?: () => void;
}

export default function DataAudit({ normalizedTableId, connectionId, onBack }: DataAuditProps) {
  const [viewMode, setViewMode] = useState<'raw' | 'readable'>('raw');
  const [columns, setColumns] = useState<any[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [tableName, setTableName] = useState('');
  const [loading, setLoading] = useState(false);
  const [unresolvedFields, setUnresolvedFields] = useState<string[]>([]);
  const [dictDialogKey, setDictDialogKey] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getSyncedData({ normalizedTableId, viewMode, page, pageSize });
      setColumns(result.columns);
      setRows(result.rows);
      setTotal(result.total);
      setTableName((result as any).table_name || '');
      setUnresolvedFields((result as any).unresolved_fields || []);
    } catch (e) {
      console.error('获取数据失败:', e);
    } finally {
      setLoading(false);
    }
  }, [normalizedTableId, viewMode, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleViewMode = () => {
    setViewMode(prev => prev === 'raw' ? 'readable' : 'raw');
    setPage(1);
  };

  return (
    <div>
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <h2 className="text-base font-semibold">数据审计 {tableName && `— ${tableName}`}</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'raw' ? 'secondary' : 'default'}
            onClick={toggleViewMode}
          >
            {viewMode === 'raw' ? '原始数据视图' : '直观查阅视图'}
          </Button>
          {connectionId && (
            <Button variant="outline" onClick={() => setDictDialogKey('__select')}>
              字典对照绑定
            </Button>
          )}
        </div>
      </div>

      <div className="p-6">
        {unresolvedFields.length > 0 && (
          <div className="bg-[var(--color-warning-light)] border border-[var(--color-warning)]/30 rounded-md px-3 py-2 mb-3 text-[13px]">
            以下字段未建立字典对照: {unresolvedFields.map(f => (
              <span
                key={f}
                className="cursor-pointer underline mr-2"
                onClick={() => connectionId && setDictDialogKey(`${tableName}.${f}`)}
              >
                {f}
              </span>
            ))}
          </div>
        )}

        {loading ? (
          <p className="text-muted-foreground">加载中...</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table className="text-[13px]">
                <TableHeader>
                  <TableRow>
                    {columns.map(col => (
                      <TableHead key={col.name} className="whitespace-nowrap">
                        {col.resolved_label || col.label}
                        <br />
                        <span className="text-[11px] text-muted-foreground/60 font-normal">{col.type}</span>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, i) => (
                    <TableRow key={i}>
                      {columns.map(col => (
                        <TableCell key={col.name} className="max-w-[200px] truncate">
                          {row[col.name] != null
                            ? String(row[col.name])
                            : <span className="text-muted-foreground/40">NULL</span>
                          }
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="text-center py-6 text-muted-foreground">
                        暂无数据
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="mt-3 flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>上一页</Button>
              <span className="text-sm text-muted-foreground">
                第 {page} 页 / 共 {Math.ceil(total / pageSize)} 页 (共 {total} 条)
              </span>
              <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(p => p + 1)}>下一页</Button>
            </div>
          </>
        )}

        {dictDialogKey && connectionId && (
          <DictMappingDialog
            connectionId={connectionId}
            sourceDictKey={dictDialogKey}
            onClose={() => setDictDialogKey(null)}
            onSaved={fetchData}
          />
        )}
      </div>
    </div>
  );
}
